import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, AppBar, Toolbar, Typography, IconButton,
  Avatar, Menu, MenuItem, Badge, Tooltip, Divider,
  BottomNavigation, BottomNavigationAction, Paper,
  useTheme, useMediaQuery, ListSubheader,
} from "@mui/material";
import {
  Home, People, SportsEsports, MeetingRoom, Receipt,
  AccountBalanceWallet, TrendingUp, Notifications, Casino,
  Settings, Menu as MenuIcon, Payment, AdminPanelSettings,
  VideoFile, AssignmentInd, ChevronLeft, Close as CloseIcon,
  Logout,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { logout } from "../../store/slices/authSlice";

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 64;

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface NavCategory {
  category: string;
  items: NavItem[];
}

const navCategories: NavCategory[] = [
  {
    category: "Overview",
    items: [
      { label: "Dashboard", icon: <Home />, path: "/" },
      { label: "Users", icon: <People />, path: "/users" },
      { label: "Admins", icon: <AdminPanelSettings />, path: "/admins" },
      { label: "Agent Requests", icon: <AssignmentInd />, path: "/agent-requests" },
    ],
  },
  {
    category: "Finance",
    items: [
      { label: "Deposits", icon: <AccountBalanceWallet />, path: "/deposits" },
      { label: "Withdrawals", icon: <TrendingUp />, path: "/withdrawals" },
      { label: "Transactions", icon: <Receipt />, path: "/transactions" },
      { label: "Payment Methods", icon: <Payment />, path: "/payment-methods" },
    ],
  },
  {
    category: "Gaming",
    items: [
      { label: "Rooms", icon: <MeetingRoom />, path: "/rooms" },
      { label: "Tournaments", icon: <SportsEsports />, path: "/tournaments" },
      { label: "Winning Patterns", icon: <Casino />, path: "/winning-patterns" },
    ],
  },
  {
    category: "System",
    items: [
      { label: "Media", icon: <VideoFile />, path: "/media" },
      { label: "Settings", icon: <Settings />, path: "/settings" },
    ],
  },
];

const allNavItems = navCategories.flatMap((c) => c.items);

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);

  const drawerWidth = collapsed && !isMobile ? DRAWER_COLLAPSED : DRAWER_WIDTH;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  // Determine current active page label
  const currentPageTitle = allNavItems.find((n) => n.path === location.pathname)?.label || "Dashboard";

  // Check if current page is one of the primary bottom nav items
  const primaryBottomPaths = ["/", "/users", "/deposits", "/withdrawals"];
  const isPrimaryTab = primaryBottomPaths.includes(location.pathname);
  const bottomNavValue = isPrimaryTab ? location.pathname : "more";

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.paper" }}>
      {/* Header area in drawer */}
      <Toolbar
        sx={{
          justifyContent: collapsed && !isMobile ? "center" : "space-between",
          px: 2,
          minHeight: { xs: 56, sm: 64 },
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {(!collapsed || isMobile) && (
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                fontSize: 14,
                fontWeight: 800,
                boxShadow: "0 2px 8px rgba(99, 102, 241, 0.4)",
              }}
            >
              IB
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.5, color: "primary.main", lineHeight: 1.1 }}>
                Ibex Games
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                Admin Portal
              </Typography>
            </Box>
          </Box>
        )}

        {isMobile ? (
          <IconButton size="small" onClick={() => setMobileOpen(false)} sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={() => setCollapsed(!collapsed)} sx={{ ml: collapsed ? 0 : "auto" }}>
            <ChevronLeft sx={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </IconButton>
        )}
      </Toolbar>

      {/* Nav items list */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1, px: 1 }}>
        {navCategories.map((group) => (
          <List
            key={group.category}
            dense
            disablePadding
            subheader={
              (!collapsed || isMobile) ? (
                <ListSubheader
                  sx={{
                    bgcolor: "transparent",
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    lineHeight: "28px",
                    px: 1.5,
                  }}
                >
                  {group.category}
                </ListSubheader>
              ) : undefined
            }
          >
            {group.items.map((item) => {
              const active = location.pathname === item.path;
              return (
                <ListItem key={item.label} disablePadding sx={{ mb: 0.4 }}>
                  <Tooltip title={collapsed && !isMobile ? item.label : ""} placement="right">
                    <ListItemButton
                      selected={active}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        borderRadius: 2,
                        justifyContent: collapsed && !isMobile ? "center" : "flex-start",
                        px: collapsed && !isMobile ? 1 : 1.5,
                        minHeight: 42,
                        transition: "all 0.15s ease",
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          fontWeight: 700,
                          boxShadow: "0 2px 8px rgba(99, 102, 241, 0.35)",
                          "&:hover": { bgcolor: "primary.dark" },
                          "& .MuiListItemIcon-root": { color: "primary.contrastText" },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed && !isMobile ? 0 : 36,
                          mr: collapsed && !isMobile ? 0 : 0.5,
                          color: active ? "primary.contrastText" : "text.secondary",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {(!collapsed || isMobile) && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "0.83rem",
                            fontWeight: active ? 700 : 500,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
            <Divider sx={{ my: 1, opacity: 0.1 }} />
          </List>
        ))}
      </Box>

      {/* User info at bottom of drawer */}
      {(!collapsed || isMobile) && (
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            bgcolor: "rgba(255,255,255,0.02)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: 14, fontWeight: 700 }}>
              {user?.fullName?.[0]?.toUpperCase() || "A"}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.fullName || "Admin"}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize", fontSize: "0.7rem" }}>
                {user?.role || "admin"}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleLogout} sx={{ color: "error.light" }} title="Logout">
            <Logout fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          transition: theme.transitions.create(["width", "margin"], { duration: 200 }),
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2.5 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 1, color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 800,
              fontSize: { xs: "1rem", sm: "1.2rem" },
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "text.primary",
            }}
          >
            {currentPageTitle}
          </Typography>

          <IconButton color="inherit" sx={{ mr: 0.5, color: "text.secondary" }}>
            <Badge badgeContent={0} color="error">
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>

          <IconButton
            color="inherit"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {user?.fullName?.[0]?.toUpperCase() || "A"}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled sx={{ fontSize: 13, fontWeight: 600 }}>
              {user?.fullName || "Admin"} ({user?.role || "admin"})
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ fontSize: 13, color: "error.main" }}>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer (Temporary) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            bgcolor: "background.paper",
            borderRight: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer (Permanent) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", { duration: 200 }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            bgcolor: "background.paper",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            transition: theme.transitions.create("width", { duration: 200 }),
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          minHeight: "100vh",
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          transition: theme.transitions.create(["width", "margin"], { duration: 200 }),
          overflowX: "hidden",
          pb: { xs: "calc(72px + env(safe-area-inset-bottom, 0px))", md: 3 },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            backgroundColor: "rgba(18, 18, 24, 0.96)",
            backdropFilter: "blur(20px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <BottomNavigation
            showLabels
            value={bottomNavValue}
            onChange={(_, newValue) => {
              if (newValue === "more") {
                setMobileOpen(true);
              } else {
                handleNavigation(newValue);
              }
            }}
            sx={{
              backgroundColor: "transparent",
              height: 58,
              "& .MuiBottomNavigationAction-root": {
                minWidth: 0,
                padding: "4px 0",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                },
                "& .MuiBottomNavigationAction-label": {
                  fontSize: "0.68rem",
                  fontWeight: 500,
                  "&.Mui-selected": {
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  },
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.25rem",
                },
              },
            }}
          >
            <BottomNavigationAction label="Dashboard" value="/" icon={<Home />} />
            <BottomNavigationAction label="Users" value="/users" icon={<People />} />
            <BottomNavigationAction label="Deposits" value="/deposits" icon={<AccountBalanceWallet />} />
            <BottomNavigationAction label="Withdrawals" value="/withdrawals" icon={<TrendingUp />} />
            <BottomNavigationAction label="More" value="more" icon={<MenuIcon />} />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default Layout;
